import { Link, Navigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import type { CustomerRef } from '@portal-contract';
import { Badge } from '@/lib/macondo';
import { useI18n } from '@/i18n/I18nProvider';
import { defaultDestination, useSession } from '@/session/SessionProvider';
import { compareCurrencies, formatMoney } from '@/lib/format';
import { PageHeading } from '@/components/PageHeading';
import { Card } from '@/components/Card';

interface Node {
  customer: CustomerRef;
  children: Node[];
}

/** Parents with their sub-customers underneath; a sub-customer whose parent is not in the set is a root. USD first, then name. */
export function buildTree(customers: readonly CustomerRef[]): Node[] {
  const ids = new Set(customers.map((c) => c.id));
  const byParent = new Map<string, CustomerRef[]>();
  const roots: CustomerRef[] = [];
  for (const c of customers) {
    if (c.parentId && ids.has(c.parentId)) byParent.set(c.parentId, [...(byParent.get(c.parentId) ?? []), c]);
    else roots.push(c);
  }
  const order = (a: CustomerRef, b: CustomerRef) => compareCurrencies(a.currency, b.currency) || a.name.localeCompare(b.name);
  const build = (c: CustomerRef): Node => ({ customer: c, children: (byParent.get(c.id) ?? []).sort(order).map(build) });
  return roots.sort(order).map(build);
}

/** `/accounts`: shown when the address opens more than one customer. A single customer goes straight to its overview. */
export function AccountsScreen() {
  const { t } = useI18n();
  const { customers } = useSession();
  if (customers.length === 1) return <Navigate to={defaultDestination(customers)} replace />;
  const tree = buildTree(customers);
  return (
    <div className="mx-auto w-full max-w-[640px]" data-screen="AccountsScreen">
      <PageHeading>{t('picker.heading')}</PageHeading>
      <p className="text-body text-typography-secondary mt-200">{t('picker.intro')}</p>
      <ul className="mt-500 flex flex-col gap-300">
        {tree.map((n) => (
          <AccountItem key={n.customer.id} node={n} depth={0} />
        ))}
      </ul>
    </div>
  );
}

function AccountItem({ node, depth }: { node: Node; depth: number }) {
  const { t, lang } = useI18n();
  const { customer: c } = node;
  const overdue = c.overdue.amount > 0;
  return (
    <li>
      <Card className={depth > 0 ? 'ml-500 sm:ml-800' : ''}>
        <Link
          to={`/a/${encodeURIComponent(c.id)}`}
          className="focus-ring hover:bg-surface-hover flex min-h-[64px] items-center justify-between gap-300 rounded-card-sm p-400"
        >
          <span className="flex min-w-0 flex-col gap-100">
            <span className="text-headline text-typography-primary break-words">{c.name}</span>
            <span className="text-callout text-typography-secondary">
              {c.currency} <span aria-hidden="true">·</span> {t('picker.balance', { amount: formatMoney(c.balance, lang) })}
            </span>
            {overdue && (
              <Badge variant="critical" className="w-fit">
                <AlertTriangle size={14} aria-hidden="true" />
                {t('picker.overdue', { amount: formatMoney(c.overdue, lang) })}
              </Badge>
            )}
          </span>
          <ChevronRight size={20} aria-hidden="true" className="text-typography-tertiary shrink-0" />
        </Link>
      </Card>
      {node.children.length > 0 && (
        <ul className="mt-300 flex flex-col gap-300">
          {node.children.map((ch) => (
            <AccountItem key={ch.customer.id} node={ch} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}
